import type { PasskeySummary, SessionUser } from '../../shared/types'
import { ApiError, deletePasskey, listPasskeys } from '../api'
import { buildFrame } from '../frame'
import { saucePicker } from '../saucepicker'
import { describeWebAuthnError, registerPasskey } from '../session'
import { formatRelative, h, plural, toast } from '../ui'

export function passkeysView(root: HTMLElement, user: SessionUser): () => void {
  const frame = buildFrame({ user, active: 'passkeys' })
  let disposed = false
  let passkeys: PasskeySummary[] = []

  const nameInput = h('input', {
    class: 'title-input',
    type: 'text',
    placeholder: 'Where will this one live? (e.g. 1Password, work laptop)',
    maxlength: '60',
    'aria-label': 'Passkey name',
  })

  const addButton = h(
    'button',
    { class: 'control primary', type: 'button', onclick: () => void add() },
    ['Add a passkey'],
  )

  frame.controls.append(nameInput, saucePicker(() => {}), addButton)

  const body = h('div', { class: 'listing' })
  frame.stage.append(body)
  root.append(frame.element)
  document.title = 'passkeys · pastabin'
  frame.statusLeft.textContent = 'loading…'

  void load()

  async function load(): Promise<void> {
    try {
      const result = await listPasskeys()
      if (disposed) return
      passkeys = result.passkeys
      renderRows()
    } catch (error) {
      if (disposed) return
      body.replaceChildren(
        h('div', { class: 'notice' }, [
          h('h1', {}, ['Could not load your passkeys']),
          h('p', {}, [error instanceof ApiError ? error.message : 'Something went wrong.']),
        ]),
      )
      frame.statusLeft.textContent = 'error'
    }
  }

  async function add(): Promise<void> {
    const name = nameInput.value.trim()
    if (!name) {
      toast('Name it first, so you can tell them apart later')
      nameInput.focus()
      return
    }
    addButton.disabled = true
    try {
      await registerPasskey({ name })
      if (disposed) return
      nameInput.value = ''
      toast('Passkey added')
      await load()
    } catch (error) {
      toast(describeWebAuthnError(error))
    } finally {
      addButton.disabled = false
    }
  }

  function renderRows(): void {
    frame.statusLeft.textContent = plural(passkeys.length, 'passkey')

    const only = passkeys.length <= 1
    const warning = only
      ? h('div', { class: 'banner' }, [
          h('b', {}, ['One passkey.']),
          h('span', {}, [
            'Lose it and you lose the account — the setup code only works while no passkey exists. Add a second one, on a different device or in a password manager.',
          ]),
        ])
      : null

    const table = h('table', { class: 'table' }, [
      h('thead', {}, [
        h('tr', {}, [
          h('th', {}, ['Name']),
          h('th', {}, ['Kind']),
          h('th', { class: 'hide-sm' }, ['Added']),
          h('th', {}, ['Last used']),
          h('th', {}, ['']),
        ]),
      ]),
      h(
        'tbody',
        {},
        passkeys.map((passkey) => {
          const tr = h('tr', {}, [
            h('td', {}, [h('span', { class: 'row-title' }, [passkey.name])]),
            h('td', {}, [
              passkey.backedUp
                ? h('span', { class: 'badge public', title: 'Synced by a password manager or platform account' }, [
                    'synced',
                  ])
                : h('span', { class: 'badge private', title: 'Lives only on one device' }, [
                    'device-only',
                  ]),
            ]),
            h('td', { class: 'hide-sm muted', title: new Date(passkey.createdAt).toLocaleString() }, [
              formatRelative(passkey.createdAt),
            ]),
            h('td', { class: 'muted' }, [
              passkey.lastUsedAt === null ? 'never' : formatRelative(passkey.lastUsedAt),
            ]),
            h('td', { class: 'row-actions' }, [
              h(
                'button',
                {
                  class: 'control',
                  type: 'button',
                  disabled: only,
                  title: only ? 'Register another passkey before removing this one' : undefined,
                  onclick: () => void remove(passkey, tr),
                },
                ['Remove'],
              ),
            ]),
          ])
          return tr
        }),
      ),
    ])

    body.replaceChildren(...(warning ? [warning, table] : [table]))
  }

  async function remove(passkey: PasskeySummary, tr: HTMLElement): Promise<void> {
    if (!confirm(`Remove “${passkey.name}”? You won't be able to sign in with it again.`)) return
    try {
      await deletePasskey(passkey.id)
      if (disposed) return
      passkeys = passkeys.filter((candidate) => candidate.id !== passkey.id)
      tr.remove()
      renderRows()
      toast('Removed')
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'Could not remove that passkey')
    }
  }

  return () => {
    disposed = true
  }
}
